import Loader from '@components/shared/loader/Loader';
import { DealsheetService } from '@services/dealsheet/DealsheetService';
import { InfoToast } from '@services/utils/ToastUtils';
import { theme } from '@theme';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useReactivateDeals } from '../../../../hooks/header/UseReactivateDeals';
import { useCheckDevice } from '../../../../hooks/UseCheckDevice';
import { AppState } from '../../../../store/AppState';
import { DealerState } from '../../../../types/DealerState';
import { Contract } from '../../../../types/header/Contract';
import { SearchResult } from '../../../../types/header/SearchResult';
import { SalesPerson } from '../../../../types/SalesPerson';
import ReactivateDealInfo from './ReactivateDealInfo';
import ReactivateDealModalHeader from './ReactivateDealModalHeader';
import ReactivateDealVehicleInfo from './ReactivateDealVehicleInfo';

type ReactivateDealModalProps = {
  searchResult: SearchResult;
  shouldOpenModal: boolean;
  setShouldOpenModal: Dispatch<SetStateAction<boolean>>;
  onReactivateDeal: () => void;
};

const ReactivateDealModal: FC<ReactivateDealModalProps> = ({
  shouldOpenModal,
  searchResult,
  setShouldOpenModal,
  onReactivateDeal,
}) => {
  const [contract, setContract] = useState<Contract>();
  const [vehicleImg, setVehicleImg] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [salesPerson, setSalesPerson] = useState<SalesPerson>({
    userId: 0,
    name: 'N/A',
    canViewDealers: [],
    firstName: '',
    lastName: '',
  });

  const { canReactivateDeal, updateMake, updateSoldBy, updateVehicleType } = useReactivateDeals();
  const { salesPersons } = useSelector<AppState, DealerState>((state) => state.dealer);
  const { isTabletDevice } = useCheckDevice();
  const modalStyles = {
    content: {
      top: '60px',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      marginRight: '-50%',
      transform: 'translate(-50%, 0%)',
      width: '92%',
      minHeight: '500px',
      minWidth: isTabletDevice ? '80%' : '900px',
      zIndex: 100000,
      padding: '0',
      maxWidth: isTabletDevice ? '99%' : '1000px',
      overflow: 'hidden',
    },
  };

  const setAssignedSalesPerson = (salesPersonId: number): void => {
    salesPersons.forEach((e: SalesPerson) => {
      if (e.userId === salesPersonId) {
        setSalesPerson(e);
      }
    });
  };

  const onReactivateDealClick = (): void => {
    onReactivateDeal();
    InfoToast('The deal reactivation process has begun; please stand by.');
    setShouldOpenModal(false);
  };

  const onCloseClick = (): void => {
    setShouldOpenModal(false);
  };

  useEffect(() => {
    if (shouldOpenModal) {
      setIsLoading(true);
      DealsheetService.getDeactivatedContract(
        searchResult.dealerId,
        searchResult.closedDealId,
        searchResult.chromeId,
        searchResult.amVehicleId,
      )
        .then((response: Contract) => {
          const updatedContract = response;
          if (searchResult.make) {
            updateMake(updatedContract, searchResult.make);
          }
          updateSoldBy(updatedContract);
          updateVehicleType(updatedContract);
          setAssignedSalesPerson(updatedContract.assignedToUserId);
          setVehicleImg(
            `https://static.findmyupgrade.com/stockimage/${updatedContract.chromeId}.jpg`,
          );
          setContract(updatedContract);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [shouldOpenModal]);

  return (
    <>
      <Modal
        data-testid='reactivate-deal-modal'
        isOpen={shouldOpenModal}
        style={modalStyles}
        contentLabel='Reactivate Modal'
        ariaHideApp={false}
      >
        {isLoading && <Loader />}
        {!isLoading && (
          <div className='manage-deal-modal'>
            <ReactivateDealModalHeader contract={contract} onCloseClick={onCloseClick} />
            <div className='modal-body'>
              <div className='container-fluid'>
                <div className='row'>
                  <ReactivateDealInfo contract={contract} salesPerson={salesPerson} />
                  <ReactivateDealVehicleInfo contract={contract} vehicleImg={vehicleImg} />
                </div>
              </div>
            </div>
            <ModalFooter>
              <div className='row text-end'>
                <div className='col-sm-12 col-xs-12'>
                  <ButtonWrapper
                    data-testid='reactivate-deal-button'
                    onClick={onReactivateDealClick}
                    disabled={!canReactivateDeal(contract, searchResult?.amVehicleId)}
                    canReactivateDeal={canReactivateDeal(contract, searchResult?.amVehicleId)}
                  >
                    REACTIVATE DEAL
                  </ButtonWrapper>
                </div>
              </div>
            </ModalFooter>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ReactivateDealModal;

type ButtonWrapperProps = {
  canReactivateDeal: boolean;
};

const ButtonWrapper = styled.button.attrs<ButtonWrapperProps>({
  className: 'btn btn-primary',
})<ButtonWrapperProps>`
  color: ${theme.palette.white};
  background-color: ${(props) =>
    props.canReactivateDeal ? theme.palette.text.purple : theme.palette.disabledGrey};
`;

const ModalFooter = styled.div.attrs({
  className: 'modal-footer',
})`
  padding: 20px;
`;
